<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import Dialog from 'primevue/dialog';
import { useToast } from 'primevue/usetoast';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useDateTime } from '@/composables';
import { THERAPY_EVENT_ICONS, EMIT } from '@/enum';
import { useCalendarBulkAttendanceStore } from '@/store';
import { TherapyEventInterface } from '@/types';

const calendarBulkAttendanceStore = useCalendarBulkAttendanceStore();
const { date, events, bulkAttendanceTitle, therapyTypes } = storeToRefs(calendarBulkAttendanceStore);

/**
 * The data ref object used to create the request payload.
 */
const data = ref({
  checkedEvents: [],
  therapyType: '',
  notes: '',
});

/**
 * The required validation rules.
 */
const rules = {
  checkedEvents: { required },
  therapyType: { required },
  notes: { required },
};

/**
 * Initialize the vuelidate instance.
 */
const v$ = useVuelidate(rules, data);

/**
 * Initialize the instance instance.
 */
const toast = useToast();

/**
 * Throw all errors here for easier access.
 */
const errors = ref(null);

/**
 * Determines whether or not the form has been submitted.
 */
const submitted: boolean = ref(false);

/**
 * Define all the available emit events.
 */
const emit = defineEmits([EMIT.CANCEL, EMIT.SAVE]);

/**
 * Whether or not to display the modal.
 */
const displayModal = ref(true);

/**
 * The check all status indicator.
 */
const checkAllEvents = ref(false);

/**
 * Initialize everything required to build out the component.
 */
onMounted(() => init());

/**
 * check whether or not there are any events available that can be bulk documented.
 */
const hasEvents = computed<boolean>(() => !!events.value.length);

/**
 * Define the request payload.
 */
const payload = computed(() => {
  const value = data.value;

  return {
    event_date: useDateTime().formatDateForApi(date.value),
    therapy_events_ids: value.checkedEvents,
    therapy_result_type_id: value.therapyType,
    notes: value.notes,
  };
});

/**
 * Initialize and render the bulk attendance component with selected events and fields.
 */
const init = () => calendarBulkAttendanceStore.getTherapyResultTypes();

/**
 * Get the field styles based on the specific key.
 */
const fieldStyles: string = (key: string, styles: string) => {
  const validation =
    (v$.value[key].$invalid && submitted.value) || (errors.value && errors.value[key]) ? 'p-invalid' : '';
  const classes = styles ? `${styles} ` : '';
  return `${classes}${validation}`;
};

/**
 * Get a comma separated list of attendees for the provided event.
 */
const attendees: string = (event: TherapyEventInterface) => event.attendees.join(',');

/**
 * Get the specific icon for the event based on the provided event type by enum.
 */
const icon = (event) => THERAPY_EVENT_ICONS[event.therapy_event_type.replaceAll(' ', '_').toUpperCase()];

/**
 * Display the toast message.
 */
const doToast = (error: boolean) => {
  toast.add({
    severity: error ? 'error' : 'success',
    summary: error ? 'Error' : 'Saved',
    detail: error ? error : 'The selected events have been documented.',
    life: 3000,
  });
};

/**
 * After a successful save, close the modal.
 */
const doSave = (response) => (doToast(response), onCancel());

/**
 * Do the following after a failed save request.
 * Set the error ref object with the error response.
 * Display the toast message.
 */
const doFail = (response) => {
  submitted.value = false;
  errors.value = response.data.errors;
  doToast(response.data.message);
};

/**
 * Handle the check/uncheck all event in order to populate the event identifiers collection.
 */
const onCheckAll = (event) => (v$.value.checkedEvents.$model = event ? events.value.map((item) => item.id) : []);

/**
 * Handle a single check event which only has a purpose to diable the check all option.
 */
const onCheck = (event) => (checkAllEvents.value = event.length);

/**
 * Handle the cancellation of the bulk attendance modal.
 */
const onCancel = () => emit('cancel');

/**
 * Handle the saving of the bulk attendance modal.
 */
const onSave = () => {
  submitted.value = true;

  // Don't allow a submit event unless the fields are valid.
  if (v$.value.$invalid) return;

  calendarBulkAttendanceStore
    .bulkAttendance(payload.value)
    .then(() => doSave())
    .catch((response) => doFail(response));

  emit('save');
};
</script>

<template>
  <div>
    <Dialog
      v-model:visible="displayModal"
      :modal="true"
      :header="bulkAttendanceTitle"
      :draggable="false"
      :breakpoints="{ '960px': '75vw', '640px': '150vw' }"
      :style="{ width: '40vw' }"
      @hide="onCancel"
    >
      <form v-if="hasEvents" class="card">
        <div
          :class="`field border-round-md ${
            fieldStyles('checkedEvents') === 'p-invalid' ? 'border-red' : 'border-white'
          } border-2 border-solid bg-white pt-3 pr-3 pb-1 pl-3 bulk-height overflow-auto`"
        >
          <div class="field-checkbox mb-2">
            <Checkbox v-model="checkAllEvents" :binary="true" :disabled="submitted" @input="onCheckAll" />
            <label for="events">
              <span class="font-semibold">Select/Deselect All</span>
            </label>
          </div>
          <div v-for="(item, key) in events" :key="key" class="field-checkbox mb-2">
            <Checkbox
              v-model="v$.checkedEvents.$model"
              name="event"
              :value="item.id"
              :disabled="submitted"
              @input="onCheck"
            />
            <label for="events">
              <div class="calendar-item">
                <span class="font-semibold" v-text="item.timeframe" />
                <Icon :name="icon(item)" size="text-sm" class="mr-1 ml-1 text-teal-600" />
                <span v-text="attendees(item)" />
              </div>
            </label>
          </div>
        </div>
        <div class="field mt-3">
          <label class="required" for="attendanceType">Attendance Type</label>
          <Dropdown
            id="calendar_bulk_attendance_therapy_type"
            v-model="v$.therapyType.$model"
            :options="therapyTypes.options"
            option-label="name"
            option-value="value"
            :loading="therapyTypes.loading"
            placeholder="Select an Attendance Type"
            :class="fieldStyles('therapyType', 'w-full')"
            :disabled="submitted"
          />
        </div>
        <div class="field mt-3">
          <label class="required" for="firstName">Notes</label>
          <InputText
            id="calendar_bulk_notes"
            v-model="v$.notes.$model"
            type="text"
            placeholder="Enter Notes"
            :class="fieldStyles('notes', 'w-full')"
            :disabled="submitted"
          />
        </div>
      </form>
      <div v-else class="p-4 text-center">There are no events available to document</div>
      <template v-if="hasEvents" #footer>
        <div class="flex justify-content-end">
          <AppButton type="secondary" text="Cancel" class="mr-2" :disabled="submitted" @click="onCancel" />
          <AppButton type="primary" text="Save" :disabled="v$.$invalid || submitted" @click="onSave" />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<style lang="scss" scoped>
.calendar-item {
  display: flex;
  align-items: flex-start;
  padding: 4px;
  background: var(--primary-color-extra-light);
  border-radius: 6px;
}

.bulk-height {
  max-height: 200px;
}
</style>
